export const FLOOR_PLAN_IMAGE_URL = '/images/building-floor-plant.png';

export const DEFAULT_FLOOR_PLAN_RECT_SIZE = 0.05;
export const MIN_FLOOR_PLAN_ZOOM = 0.5;
export const MAX_FLOOR_PLAN_ZOOM = 3;
export const FLOOR_PLAN_ZOOM_STEP = 0.25;
/** Zoom ao abrir/centralizar a vaga — o suficiente para achar o retângulo sem perder o contexto da planta. */
export const VIEW_FOCUS_ZOOM = 1.5;

export interface FloorPlanRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FloorPlanLocationFields {
  locationX?: number | null;
  locationY?: number | null;
  locationWidth?: number | null;
  locationHeight?: number | null;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function hasFloorPlanLocation(spot: FloorPlanLocationFields): boolean {
  return spot.locationX != null && spot.locationY != null;
}

export function floorPlanRectFromLocation(
  spot: FloorPlanLocationFields,
): FloorPlanRect | null {
  if (!hasFloorPlanLocation(spot)) return null;
  return {
    x: Number(spot.locationX),
    y: Number(spot.locationY),
    width: Number(spot.locationWidth) || DEFAULT_FLOOR_PLAN_RECT_SIZE,
    height: Number(spot.locationHeight) || DEFAULT_FLOOR_PLAN_RECT_SIZE,
  };
}

export function formatFloorPlanRect(rect: FloorPlanRect): string {
  return `${Math.round(rect.x * 100)}%, ${Math.round(rect.y * 100)}% · ${Math.round(rect.width * 100)}% × ${Math.round(rect.height * 100)}%`;
}

export function normalizeRect(x0: number, y0: number, x1: number, y1: number): FloorPlanRect {
  const left = clamp01(Math.min(x0, x1));
  const top = clamp01(Math.min(y0, y1));
  const right = clamp01(Math.max(x0, x1));
  const bottom = clamp01(Math.max(y0, y1));
  return {
    x: left,
    y: top,
    width: Math.max(right - left, 0.002),
    height: Math.max(bottom - top, 0.002),
  };
}

export function rectFromClick(
  x: number,
  y: number,
  size = DEFAULT_FLOOR_PLAN_RECT_SIZE,
): FloorPlanRect {
  const half = size / 2;
  return {
    x: clamp01(x - half),
    y: clamp01(y - half),
    width: size,
    height: size,
  };
}

export function containScale(
  naturalWidth: number,
  naturalHeight: number,
  boxWidth: number,
  boxHeight: number,
): number {
  if (naturalWidth <= 0 || naturalHeight <= 0 || boxWidth <= 0 || boxHeight <= 0) {
    return 1;
  }
  return Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
}

export function displayedImageSize(
  naturalWidth: number,
  naturalHeight: number,
  boxWidth: number,
  boxHeight: number,
  zoom: number,
): { width: number; height: number; scale: number } {
  const scale = containScale(naturalWidth, naturalHeight, boxWidth, boxHeight);
  return {
    width: naturalWidth * scale * zoom,
    height: naturalHeight * scale * zoom,
    scale,
  };
}

export function zoomToCenterRect(): number {
  return VIEW_FOCUS_ZOOM;
}

export function scrollOffsetToCenterRect(
  rect: FloorPlanRect,
  imageWidth: number,
  imageHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): { left: number; top: number } {
  const cx = (rect.x + rect.width / 2) * imageWidth;
  const cy = (rect.y + rect.height / 2) * imageHeight;
  return {
    left: clamp(cx - viewportWidth / 2, 0, Math.max(0, imageWidth - viewportWidth)),
    top: clamp(cy - viewportHeight / 2, 0, Math.max(0, imageHeight - viewportHeight)),
  };
}
